import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { buildApiUrl } from "./config";

const SUBJECT_OPTIONS = [
  "Mathematics", "English", "Science", "Biology", "Chemistry", "Physics",
  "History", "Geography", "Arabic", "Islamic Studies", "Computer Science",
  "Art", "Music", "Physical Education", "Other"
];

const UploadHomeworkComponent = ({ assignedForms = [] }) => {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [subject, setSubject] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [file, setFile] = useState(null);
  const [assignedTo, setAssignedTo] = useState([]);
  const [uploading, setUploading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = [];
    if (!title) errors.push("Title is required.");
    if (!subject) errors.push("Subject is required.");
    if (assignedTo.length === 0) errors.push("Please assign to at least one form.");
    if (!file || !dueDate) errors.push("All fields are required including file.");

    if (errors.length > 0) {
      alert(errors.join("\n"));
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("title", title);
    formData.append("subject", subject);
    formData.append("dueDate", dueDate);
    formData.append("file", file);
    formData.append("assignedTo", JSON.stringify(assignedTo));

    const createdAt = new Date().toISOString();
    const createdBy = localStorage.getItem("teacherEmail") || "unknown";
    formData.append("createdAt", createdAt);
    formData.append("createdBy", createdBy);

    try {
      const res = await fetch(buildApiUrl("/upload-homework"), {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      if (res.ok) {
        alert("Homework uploaded successfully!");
        navigate('/teacher?mainTab=assignments&tab=my-assignments&module=assignments');
      } else {
        alert("Upload failed: " + (data.detail || "Unknown error"));
      }
    } catch (err) {
      alert("Error uploading homework.");
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 space-y-5 max-w-2xl mx-auto">
      {/* Title */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">Title</label>
        <input
          type="text"
          placeholder="e.g. Chapter 5 Worksheet"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          required
        />
      </div>

      {/* Subject Dropdown */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">Subject</label>
        <select
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
          required
        >
          <option value="">Select Subject</option>
          {SUBJECT_OPTIONS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {/* Due Date */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">Due Date</label>
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          required
        />
      </div>

      {/* Assign to Forms */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Assign to Forms</label>
        {assignedForms.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No forms assigned to you. Contact admin to be assigned forms.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {assignedForms.map((form) => (
              <label key={form} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all ${
                assignedTo.includes(form) 
                  ? 'bg-blue-50 border-blue-400 text-blue-700' 
                  : 'bg-white border-gray-200 hover:border-blue-300 text-gray-700'
              }`}>
                <input
                  type="checkbox"
                  checked={assignedTo.includes(form)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setAssignedTo([...assignedTo, form]);
                    } else {
                      setAssignedTo(assignedTo.filter(f => f !== form));
                    }
                  }}
                  className="accent-blue-600"
                />
                <span className="text-sm font-medium">{form}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* File Upload */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">Upload File</label>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
          <input
            type="file"
            onChange={(e) => setFile(e.target.files[0])}
            className="w-full"
            accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
            required
          />
          {file && (
            <p className="mt-2 text-sm text-green-600 font-medium">
              📎 {file.name} ({(file.size / 1024).toFixed(1)} KB)
            </p>
          )}
        </div>
      </div>

      {/* Submit Button */}
      <button 
        type="submit" 
        disabled={uploading}
        className={`w-full py-3 rounded-xl text-white font-semibold text-base transition-all ${
          uploading 
            ? 'bg-gray-400 cursor-not-allowed' 
            : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl'
        }`}
      >
        {uploading ? (
          <span className="flex items-center justify-center gap-2">
            <span className="animate-spin">⏳</span> Uploading...
          </span>
        ) : (
          '📤 Upload Homework'
        )}
      </button>
    </form>
  );
};

export default UploadHomeworkComponent;
